import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {DEVICE_QUERY_KEY_MAP} from '../api/device/constant';
import {getAppBadge} from '../api/device/getAppBadge';
import {getDeviceList} from '../api/device/getDeviceList';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {requestContext} from '../common/requestContext';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createAppBadge} from '../graphql/factory/AppBadge';
import {createDeviceInfo} from '../graphql/factory/DeviceInfo';
import type {
  AppConfigQuery,
  AppConfigQueryVariables,
  DeviceInfoQuery,
} from '../graphql/generated/types';

/**
 * Device 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const deviceFactory = {
  /**
   * 앱 설정 조회 (appBadge 포함)
   *
   * Origin: useAppConfigQuery
   * API: GET /api/v1/app/badge
   *
   * Resolver 로직:
   * - Query.appConfig는 빈 객체 반환
   * - AppConfig.appBadge field resolver에서 실제 API 호출
   * - createAppBadge factory 적용
   */
  appConfig: (
    variables?: AppConfigQueryVariables,
    options?: Omit<
      UseQueryOptions<AppConfigQuery, PagewebError, AppConfigQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: DEVICE_QUERY_KEY_MAP.appConfig(variables),
      queryFn: async () => {
        const authToken = requestContext.requestInfo.user;

        if (!authToken) {
          return {
            appConfig: {
              appBadge: null,
            },
          };
        }

        const data = await getAppBadge(fetcherOptions);

        if (!data) {
          throw new Error('appBadge 가져오기 실패');
        }

        // Adapter의 combine 로직을 여기서 수행
        return {
          appConfig: {
            appBadge: createAppBadge(data),
          },
        };
      },
      ...options,
    }),

  /**
   * 디바이스 정보 조회
   *
   * Origin: useDeviceInfoQuery
   *
   * ⚠️ Resolver 로직:
   * - response.result가 없으면 에러 throw
   * - createDeviceInfo factory로 변환
   */
  deviceInfo: (
    options?: Omit<
      UseQueryOptions<DeviceInfoQuery, PagewebError, DeviceInfoQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: DEVICE_QUERY_KEY_MAP.deviceInfo,
      queryFn: async () => {
        const response = await getDeviceList(fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '등록된 기기 목록 조회를 할 수 없습니다.'
          );
        }

        // Adapter의 combine 로직을 여기서 수행
        return {
          deviceInfo: createDeviceInfo(response.result),
        };
      },
      ...options,
    }),
} as const;
